/**
 * The Properties panel right of the paper: the template the page was made from and its
 * parameters (a character's age, the place a scene is set in...), each written as its kind
 * says. A parameter naming a page keeps a link in the graph (the core adds and moves it when
 * the page is saved).
 */
import { For, Match, Show, Switch, createMemo, type JSX } from "solid-js";
import type { ParamDef } from "@protocol/ParamDef";
import { useApp } from "../../../app/context";
import type { EditorParts } from "./Editor";

/** The Properties panel. */
export function Properties(props: { parts: EditorParts }): JSX.Element {
  const { parts } = props;
  const { documents, graph, assets, kinds } = useApp();
  const template = createMemo(() => documents.template(parts.template()));
  const nodes = createMemo(() => [...graph.state.graph.nodes].filter((n) => n.id !== parts.node()?.id).sort((a, b) => a.name.localeCompare(b.name)));
  const pictures = createMemo(() => (assets.source("project")?.listing.files ?? []).filter((f) => f.kind === "image"));
  return (
    <aside class="doc-properties" id="doc-properties" aria-label="Properties">
      <h4>Properties</h4>
      <Show when={template()} fallback={<p class="muted">{parts.template() ? `The template "${parts.template()}" is not here any more.` : "Made without a template."}</p>}>
        {(t) => (
          <>
            <p class="muted" id="doc-template" data-template-id={t().id}>
              {t().name}
              <Show when={t().summary}>: {t().summary}</Show>
            </p>
            <For each={t().params}>{(param) => <Param param={param} />}</For>
          </>
        )}
      </Show>
    </aside>
  );

  /** One parameter's field. */
  function Param(p: { param: ParamDef }): JSX.Element {
    const value = () => parts.params()[p.param.id] ?? "";
    const set = (v: string) => parts.setParam(p.param.id, v);
    const id = `doc-param-${p.param.id}`;
    return (
      <div class="doc-param" data-param={p.param.id} data-param-type={p.param.type}>
        <label for={id}>
          {p.param.label}
          <Show when={p.param.link}>
            <span class="muted" title="A link of this kind is kept in the graph">
              {" "}
              ({kinds.ofLink({ kind: p.param.link! }).label})
            </span>
          </Show>
        </label>
        <Switch fallback={<input id={id} type="text" disabled={!parts.canEdit()} value={value()} onChange={(e) => set(e.currentTarget.value)} />}>
          <Match when={p.param.type === "long" || p.param.type === "list"}>
            <textarea id={id} rows={p.param.type === "list" ? 4 : 3} placeholder={p.param.type === "list" ? "One per line" : ""} disabled={!parts.canEdit()} value={value()} onChange={(e) => set(e.currentTarget.value)} />
          </Match>
          <Match when={p.param.type === "number"}>
            <input id={id} type="number" disabled={!parts.canEdit()} value={value()} onChange={(e) => set(e.currentTarget.value)} />
          </Match>
          <Match when={p.param.type === "date"}>
            <input id={id} type="date" disabled={!parts.canEdit()} value={value()} onChange={(e) => set(e.currentTarget.value)} />
          </Match>
          <Match when={p.param.type === "choice"}>
            <select id={id} disabled={!parts.canEdit()} value={value()} onChange={(e) => set(e.currentTarget.value)}>
              <option value="">—</option>
              <For each={p.param.options ?? []}>{(o) => <option value={o}>{o}</option>}</For>
              <Show when={value() && !(p.param.options ?? []).includes(value())}>
                <option value={value()}>{value()}</option>
              </Show>
            </select>
          </Match>
          <Match when={p.param.type === "page"}>
            <select id={id} disabled={!parts.canEdit()} value={value()} onChange={(e) => set(e.currentTarget.value)}>
              <option value="">—</option>
              <For each={nodes()}>{(n) => <option value={n.id}>{n.name}</option>}</For>
              <Show when={value() && !nodes().some((n) => n.id === value())}>
                <option value={value()}>{value()} (not here)</option>
              </Show>
            </select>
          </Match>
          <Match when={p.param.type === "image"}>
            <select id={id} disabled={!parts.canEdit()} value={value()} onChange={(e) => set(e.currentTarget.value)}>
              <option value="">—</option>
              <For each={pictures()}>{(f) => <option value={f.path}>{f.path}</option>}</For>
            </select>
            <Show when={value() && assets.url(value())}>{(src) => <img class="doc-param-image" src={src()} alt={value()} />}</Show>
          </Match>
        </Switch>
      </div>
    );
  }
}
